import { useState } from "react";
import axios from "axios";
import { CheckCircle2, Loader2 } from "lucide-react";
import { Reveal, SectionLabel } from "./shared";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLES = [
  "A CCI or institution",
  "An organisation looking to collaborate",
  "A care leaver",
  "A donor or sponsor",
  "A volunteer or mentor",
  "An employer",
  "Media",
  "Other",
];

const INTERESTS = [
  "Reclaiming Tigerhood",
  "Careleavers Support",
  "Capacity Building",
  "Resource Network",
  "Not sure yet",
];

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

export const LeadForm = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", role: "", interest: "", message: "" });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle");

  const set = (key) => (e) => {
    const value = e?.target ? e.target.value : e;
    setForm((f) => ({ ...f, [key]: value }));
    setErrors((er) => ({ ...er, [key]: undefined }));
  };

  const validate = () => {
    const errs = {};
    if (!form.name.trim()) errs.name = "Please tell us your name.";
    if (!EMAIL_RE.test(form.email.trim())) errs.email = "Please enter a valid email address.";
    if (!form.role) errs.role = "Please choose one.";
    return errs;
  };

  const submit = async (e) => {
    e.preventDefault();
    const errs = validate();
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setStatus("sending");
    try {
      await axios.post(`${API}/leads`, {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        role: form.role,
        interest: form.interest,
        message: form.message.trim(),
      });
      setStatus("done");
    } catch {
      setStatus("error");
    }
  };

  return (
    <section id="contact" className="py-24 md:py-36 bg-[#F3EFE5]" data-testid="contact-section">
      <div className="max-w-3xl mx-auto px-6 md:px-12">
        <Reveal>
          <SectionLabel testId="contact-label">Get In Touch</SectionLabel>
          <h2
            className="font-heading text-3xl sm:text-4xl md:text-[2.75rem] font-semibold tracking-tight text-[#1F2043] leading-[1.15]"
            data-testid="contact-headline"
          >
            Let&rsquo;s talk.
          </h2>
          <p className="font-body mt-5 text-base md:text-lg font-light text-[#3C3C3B]" data-testid="contact-subline">
            Tell us who you are and what brought you here.
          </p>
        </Reveal>

        <Reveal delay={0.12}>
          <div className="card-soft mt-12 p-8 md:p-12">
            {status === "done" ? (
              <div className="text-center py-10" data-testid="lead-success-message">
                <CheckCircle2 className="w-12 h-12 text-[#00867A] mx-auto" strokeWidth={1.5} />
                <p className="font-heading mt-6 text-xl md:text-2xl font-medium text-[#1F2043]">
                  Thanks. We&rsquo;ll get back to you within 3&ndash;5 working days.
                </p>
              </div>
            ) : (
              <form onSubmit={submit} noValidate data-testid="lead-form" className="flex flex-col gap-6">
                <div>
                  <label htmlFor="lead-name" className="field-label">Full name</label>
                  <input
                    id="lead-name"
                    data-testid="lead-name-input"
                    className={`field-input ${errors.name ? "field-error" : ""}`}
                    value={form.name}
                    onChange={set("name")}
                    autoComplete="name"
                  />
                  {errors.name && <p className="mt-2 text-sm text-[#C2402E] font-light" data-testid="lead-name-error">{errors.name}</p>}
                </div>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="lead-email" className="field-label">Email</label>
                    <input
                      id="lead-email"
                      type="email"
                      data-testid="lead-email-input"
                      className={`field-input ${errors.email ? "field-error" : ""}`}
                      value={form.email}
                      onChange={set("email")}
                      autoComplete="email"
                    />
                    {errors.email && <p className="mt-2 text-sm text-[#C2402E] font-light" data-testid="lead-email-error">{errors.email}</p>}
                  </div>
                  <div>
                    <label htmlFor="lead-phone" className="field-label">Phone <span className="text-[#3C3C3B]/45 font-light">(optional)</span></label>
                    <input
                      id="lead-phone"
                      type="tel"
                      data-testid="lead-phone-input"
                      className="field-input"
                      value={form.phone}
                      onChange={set("phone")}
                      autoComplete="tel"
                    />
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="field-label">I&rsquo;m reaching out as a:</label>
                    <Select value={form.role} onValueChange={set("role")}>
                      <SelectTrigger
                        data-testid="lead-role-select"
                        className={`field-input ${errors.role ? "field-error" : ""}`}
                      >
                        <SelectValue placeholder="Choose one" />
                      </SelectTrigger>
                      <SelectContent>
                        {ROLES.map((role) => (
                          <SelectItem key={role} value={role} data-testid={`lead-role-option-${role.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                            {role}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {errors.role && <p className="mt-2 text-sm text-[#C2402E] font-light" data-testid="lead-role-error">{errors.role}</p>}
                  </div>
                  <div>
                    <label className="field-label">Which area interests you? <span className="text-[#3C3C3B]/45 font-light">(optional)</span></label>
                    <Select value={form.interest} onValueChange={set("interest")}>
                      <SelectTrigger data-testid="lead-interest-select" className="field-input">
                        <SelectValue placeholder="Choose one" />
                      </SelectTrigger>
                      <SelectContent>
                        {INTERESTS.map((interest) => (
                          <SelectItem key={interest} value={interest} data-testid={`lead-interest-option-${interest.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                            {interest}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <label htmlFor="lead-message" className="field-label">Message <span className="text-[#3C3C3B]/45 font-light">(optional)</span></label>
                  <textarea
                    id="lead-message"
                    data-testid="lead-message-input"
                    className="field-input"
                    value={form.message}
                    onChange={set("message")}
                  />
                </div>
                {status === "error" && (
                  <p className="text-sm text-[#C2402E] font-light" data-testid="lead-error-message">
                    Something went wrong on our end. Please try again.
                  </p>
                )}
                <button
                  type="submit"
                  data-testid="lead-submit-button"
                  disabled={status === "sending"}
                  className="btn-primary w-full md:w-fit disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {status === "sending" ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Sending
                    </>
                  ) : (
                    "Send message"
                  )}
                </button>
              </form>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  );
};
